import api from '@/services/api';
import { checkToken } from '@/utils/lambdaUtils';
import logger from '@/utils/logger';
import { useAuthStore } from '@/stores/authStore';
import axios from 'axios';
import { DocumentModel } from '@/models/DocumentModel';
import { apiPostForm, validateRequiredParams } from '@/services/BaseApiService';

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL + '/hce';

/**
 * Fetches transactions associated with a specific contact.
 *
 * @param {string} contactId - The ID of the contact.
 * @returns {Promise<Object>} The contact transactions data.
 * @throws {Error} If the contactId is missing or an error occurs during the API call.
 */
export const getContactTransactions = async(contactId) => {
  if (!contactId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/getcontacttransactions?id=${encodeURIComponent(contactId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting contact transaction ', error);
      throw error;
  }
};

/**
 * Fetches a specific transaction by its ID.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @returns {Promise<Object>} The transaction data.
 * @throws {Error} If the transactionId is missing or an error occurs during the API call.
 */
export const getTransaction = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransaction?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction', error);
      throw error;
  }
};

/**
 * Inserts a new transaction.
 *
 * @param {Object} transaction - The transaction data to insert.
 * @returns {Promise<Object>} The inserted transaction data.
 * @throws {Error} If the transaction data is missing or an error occurs during the API call.
 */
export const insertTransaction = async(transaction) => {
  if (!transaction) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/inserttransaction`;
    const response = await api.post(url, transaction);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction general information', error);
    throw error;
  }
};

/**
 * Updates an existing transaction.
 *
 * @param {Object} transaction - The transaction data to update.
 * @returns {Promise<Object>} The updated transaction data.
 * @throws {Error} If the transaction data is missing or an error occurs during the API call.
 */
export const updateTransaction = async(transaction) => {
  if (!transaction) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransaction`;
    const response = await api.post(url, transaction);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction general information', error);
    throw error;
  }
};

/**
 * Updates the certification details of a transaction.
 *
 * @param {Object} transaction - The transaction certification data to update.
 * @returns {Promise<Object>} The updated transaction certification data.
 * @throws {Error} If the transaction data is missing or an error occurs during the API call.
 */
export const updateTransactionCertification = async(transaction) => {
  if (!transaction) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactioncertification`;
    const response = await api.post(url, transaction);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction certification', error);
    throw error;
  }
};

/**
 * Updates the status of a transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} status - The new status of the transaction.
 * @returns {Promise<Object>} The updated transaction status data.
 * @throws {Error} If the transactionId or status is missing or an error occurs during the API call.
 */
export const updateTransactionStatus = async(transactionId, status) => {
  if (!transactionId || !status) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionstatus?id=${encodeURIComponent(transactionId)}&status=${encodeURIComponent(status)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error updating transaction status', error);
    throw error;
  }
};

/**
 * Fetches the terms of a specific transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @returns {Promise<Object>} The transaction terms data.
 * @throws {Error} If the transactionId is missing or an error occurs during the API call.
 */
export const getTransactionTerms = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionterms?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction terms', error);
      throw error;
  }
};

/**
 * Fetches the contacts associated with a specific transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @returns {Promise<Object>} The transaction contacts data.
 * @throws {Error} If the transactionId is missing or an error occurs during the API call.
 */
export const getTransactionContacts = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactioncontacts?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction contacts', error);
      throw error;
  }
};

/**
 * Fetches a specific contact associated with a transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} contactId - The ID of the contact.
 * @returns {Promise<Object>} The transaction contact data.
 * @throws {Error} If the transactionId or contactId is missing or an error occurs during the API call.
 */
export const getTransactionContact = async(transactionId, contactId) => {
  if (!transactionId || !contactId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactioncontact?id=${encodeURIComponent(transactionId)}&contactId=${encodeURIComponent(contactId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction contact', error);
    throw error;
  }
};

/**
 * Inserts a new contact into a transaction.
 *
 * @param {Object} transactionContact - The transaction contact data to insert.
 * @returns {Promise<Object>} The inserted transaction contact data.
 * @throws {Error} If the transactionContact data is missing or an error occurs during the API call.
 */
export const insertTransactionContact = async(transactionContact) => {
  if (!transactionContact) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactioncontact`;
    const response = await api.post(url, transactionContact);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction contact', error);
    throw error;
  }
};

/**
 * Updates an existing contact in a transaction.
 *
 * @param {Object} transactionContact - The transaction contact data to update.
 * @returns {Promise<Object>} The updated transaction contact data.
 * @throws {Error} If the transactionContact data is missing or an error occurs during the API call.
 */
export const updateTransactionContact = async(transactionContact) => {
  if (!transactionContact) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactioncontact`;
    const response = await api.post(url, transactionContact);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction contact', error);
    throw error;
  }
};

/**
 * Deletes a contact from a transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} contactId - The ID of the contact to delete.
 * @returns {Promise<Object>} The result of the deletion operation.
 * @throws {Error} If the transactionId or contactId is missing or an error occurs during the API call.
 */
export const deleteTransactionContact = async(transactionId, contactId) => {
  if (!transactionId || !contactId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactioncontact?transactionId=${encodeURIComponent(transactionId)}&contactId=${encodeURIComponent(contactId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction contact (ApiService)', error);
    throw error;
  }
};

/**
 * Fetches the organizations associated with a specific transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @returns {Promise<Object>} The transaction organizations data.
 * @throws {Error} If the transactionId is missing or an error occurs during the API call.
 */
export const getTransactionOrganizations = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionorganizations?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction organiztions', error);
      throw error;
  }
};

/**
 * Fetches a specific organization associated with a transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} transactionOrganizationId - The ID of the organization.
 * @returns {Promise<Object>} The transaction organization data.
 * @throws {Error} If the transactionId or transactionOrganizationId is missing or an error occurs during the API call.
 */
export const getTransactionOrganization = async(transactionId, transactionOrganizationId) => {
  if (!transactionOrganizationId || !transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionorganization?id=${encodeURIComponent(transactionId)}&organizationId=${encodeURIComponent(transactionOrganizationId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction organization', error);
      throw error;
  }
};

/**
 * Inserts a new organization into a transaction.
 *
 * @param {Object} transactionOrganization - The transaction organization data to insert.
 * @returns {Promise<Object>} The inserted transaction organization data.
 * @throws {Error} If the transactionOrganization data is missing or an error occurs during the API call.
 */
export const insertTransactionOrganization = async(transactionOrganization) => {
  if (!transactionOrganization) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactionorganization`;
    const response = await api.post(url, transactionOrganization);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction organization', error);
    throw error;
  }
};

/**
 * Updates an existing transaction organization.
 *
 * @param {Object} transactionOrganization - The transaction organization data to update.
 * @returns {Promise<Object>} The updated transaction organization data.
 * @throws {Error} If the transactionOrganization data is missing or an error occurs during the API call.
 */
export const updateTransactionOrganization = async(transactionOrganization) => {
  if (!transactionOrganization) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionorganization`;
    const response = await api.post(url, transactionOrganization);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction organization', error);
    throw error;
  }
};

/**
 * Deletes a transaction organization.
 *
 * @param {string} transactionOrganizationId - The ID of the organization to delete.
 * @returns {Promise<Object>} The result of the deletion operation.
 * @throws {Error} If the transactionOrganizationId is missing or an error occurs during the API call.
 */
export const deleteTransactionOrganization = async(transactionOrganizationId) => {
  if (!transactionOrganizationId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactionorganization?id=${encodeURIComponent(transactionOrganizationId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction organization', error);
    throw error;
  }
};

/**
 * Fetches the organization ownerships associated with a specific transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @returns {Promise<Object>} The transaction organization ownerships data.
 * @throws {Error} If the transactionId is missing or an error occurs during the API call.
 */
export const getTransactionOrganizationOwnerships = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionorganizationownerships?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction entity ownerships', error);
      throw error;
  }
};

/**
 * Fetches a specific organization ownership associated with a transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} organizationOwnershipId - The ID of the organization ownership.
 * @returns {Promise<Object>} The transaction organization ownership data.
 * @throws {Error} If the transactionId or organizationOwnershipId is missing or an error occurs during the API call.
 */
export const getTransactionOrganizationOwnership = async(transactionId, organizationOwnershipId) => {
  if (!transactionId || !organizationOwnershipId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionorganizationownership?id=${encodeURIComponent(transactionId)}&organizationOwnershipId=${encodeURIComponent(organizationOwnershipId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction entity ownership', error);
      throw error;
  }
};

/**
 * Inserts a new organization ownership in a transaction.
 *
 * @param {Object} transactionOrganizationOwnership - The transaction organization ownership data to insert.
 * @returns {Promise<Object>} The inserted transaction organization ownership data.
 * @throws {Error} If the transactionOrganizationOwnership data is missing or an error occurs during the API call.
 */
export const insertTransactionOrganizationOwnership = async(transactionOrganizationOwnership) => {
  if (!transactionOrganizationOwnership) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactionorganizationownership`;
    const response = await api.post(url, transactionOrganizationOwnership);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction entity ownership', error);
    throw error;
  }
};

/**
 * Updates an existing organization ownership in a transaction.
 *
 * @param {Object} transactionOrganizationOwnership - The transaction organization ownership data to update.
 * @returns {Promise<Object>} The updated transaction organization ownership data.
 * @throws {Error} If the transactionOrganizationOwnership data is missing or an error occurs during the API call.
 */
export const updateTransactionOrganizationOwnership = async(transactionOrganizationOwnership) => {
  if (!transactionOrganizationOwnership) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionorganizationownership`;
    const response = await api.post(url, transactionOrganizationOwnership);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction entity ownership', error);
    throw error;
  }
};

/**
 * Deletes an organization ownership from a transaction.
 *
 * @param {string} transactionOrganizationOwnershipId - The ID of the organization ownership to delete.
 * @returns {Promise<Object>} The result of the deletion operation.
 * @throws {Error} If the transactionOrganizationOwnershipId is missing or an error occurs during the API call.
 */
export const deleteTransactionOrganizationOwnership = async(transactionOrganizationOwnershipId) => {
  if (!transactionOrganizationOwnershipId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactionorganizationownership?id=${encodeURIComponent(transactionOrganizationOwnershipId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction entity ownership', error);
    throw error;
  }
};

/**
 * Fetches the leaderships associated with a specific transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @returns {Promise<Object>} The transaction leaderships data.
 * @throws {Error} If the transactionId is missing or an error occurs during the API call.
 */
export const getTransactionLeaderships = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionleaderships?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction leadership', error);
      throw error;
  }
};

/**
 * Fetches a specific leadership associated with a transaction.
 *
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} leadershipId - The ID of the leadership.
 * @returns {Promise<Object>} The transaction leadership data.
 * @throws {Error} If the transactionId or leadershipId is missing or an error occurs during the API call.
 */
export const getTransactionLeadership = async(transactionId, leadershipId) => {
  if (!transactionId || !leadershipId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionleadership?id=${encodeURIComponent(transactionId)}&leadershipId=${encodeURIComponent(leadershipId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction leadership', error);
      throw error;
  }
};

export const insertTransactionLeadership = async(transactionLeadership) => {
  if (!transactionLeadership) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactionleadership`;
    const response = await api.post(url, transactionLeadership);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction leadership', error);
    throw error;
  }
};

/**
 * Updates an existing leadership in a transaction.
 *
 * @param {Object} transactionLeadership - The transaction leadership data to update.
 * @returns {Promise<Object>} The updated transaction leadership data.
 * @throws {Error} If the transactionLeadership data is missing or an error occurs during the API call.
 */
export const updateTransactionLeadership = async(transactionLeadership) => {
  if (!transactionLeadership) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionleadership`;
    const response = await api.post(url, transactionLeadership);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction leadership', error);
    throw error;
  }
};

/**
 * Deletes a transaction leadership by its ID.
 *
 * @async
 * @function deleteTransactionLeadership
 * @param {string} transactionLeadershipId - The ID of the transaction leadership to delete.
 * @throws {Error} Throws an error if the `transactionLeadershipId` parameter is missing.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const deleteTransactionLeadership = async(transactionLeadershipId) => {
  if (!transactionLeadershipId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactionleadership?id=${encodeURIComponent(transactionLeadershipId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction leadership', error);
    throw error;
  }
};

/**
 * Fetches transaction services for a given transaction ID.
 *
 * @async
 * @function getTransactionServices
 * @param {string} transactionId - The ID of the transaction to retrieve services for.
 * @throws {Error} Throws an error if the transactionId is not provided or if the API call fails.
 * @returns {Promise<Object>} A promise that resolves to the data of the transaction services.
 */
export const getTransactionServices = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionservices?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction services', error);
      throw error;
  }
};

/**
 * Updates the transaction services for a given transaction organization.
 *
 * @async
 * @function updateTransactionServices
 * @param {string} transactionOrganizationId - The ID of the transaction organization.
 * @param {Array} services - The list of services to update.
 * @throws {Error} Throws an error if required parameters are missing or if the API call fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const updateTransactionServices = async(transactionOrganizationId, services) => {
  if (!transactionOrganizationId || !services) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionservices`;
    const response = await api.post(url, { transactionOrganizationId, services });
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction services', error);
    throw error;
  }
};

/**
 * Fetches the transaction locations for a given transaction ID.
 *
 * @async
 * @function getTransactionLocations
 * @param {string} transactionId - The ID of the transaction to retrieve locations for.
 * @throws {Error} Throws an error if the transactionId is not provided or if the API call fails.
 * @returns {Promise<Object>} A promise that resolves to the transaction locations data.
 */
export const getTransactionLocations = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionlocations?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction locations', error);
      throw error;
  }
};

/**
 * Retrieves the location details of a specific transaction.
 *
 * @async
 * @function getTransactionLocation
 * @param {string} transactionId - The unique identifier of the transaction.
 * @param {string} transactionLocationId - The unique identifier of the transaction location.
 * @throws {Error} Throws an error if required parameters are missing or if the API call fails.
 * @returns {Promise<Object>} A promise that resolves to the transaction location data.
 */
export const getTransactionLocation = async(transactionId, transactionLocationId) => {
  if (!transactionId || !transactionLocationId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionlocation?id=${encodeURIComponent(transactionId)}&transactionLocationId=${encodeURIComponent(transactionLocationId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction location', error);
      throw error;
  }
};

/**
 * Inserts a transaction location by making a POST request to the API.
 *
 * @async
 * @function insertTransactionLocation
 * @param {Object} transactionLocation - The transaction location data to be inserted.
 * @throws {Error} Throws an error if the `transactionLocation` parameter is missing.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const insertTransactionLocation = async(transactionLocation) => {
  if (!transactionLocation) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactionlocation`;
    const response = await api.post(url, transactionLocation);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction location', error);
    throw error;
  }
};

/**
 * Updates the transaction location by making a POST request to the server.
 *
 * @async
 * @function updateTransactionLocation
 * @param {Object} transactionLocation - The transaction location data to be updated.
 * @throws {Error} Throws an error if the `transactionLocation` parameter is missing.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} The response data from the server.
 */
export const updateTransactionLocation = async(transactionLocation) => {
  if (!transactionLocation) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionlocation`;
    const response = await api.post(url, transactionLocation);
    return response.data;
  }
  catch (error) {
    logger.error('Error updating transaction location', error);
    throw error;
  }
};

/**
 * Deletes a transaction location by its ID.
 *
 * @async
 * @function deleteTransactionLocation
 * @param {string} locationId - The ID of the location to be deleted.
 * @throws {Error} Throws an error if the locationId is not provided or if the API call fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const deleteTransactionLocation = async(locationId) => {
  if (!locationId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactionlocation?locationId=${encodeURIComponent(locationId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction location (ApiService)', error);
    throw error;
  }
};

/**
 * Fetches job categories for a specific transaction and location.
 *
 * @async
 * @function getTransactionLocationJobCategories
 * @param {string} transactionId - The ID of the transaction.
 * @param {string} locationId - The ID of the location.
 * @throws {Error} Throws an error if required parameters are missing or if the API call fails.
 * @returns {Promise<Object>} A promise that resolves to the response data containing job categories.
 */
export const getTransactionLocationJobCategories = async(transactionId, locationId) => {
  if (!transactionId || !locationId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/gettransactionlocationjobcategories?id=${encodeURIComponent(transactionId)}&locationId=${encodeURIComponent(locationId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction location staff', error);
      throw error;
  }
};

/**
 * Fetches all transaction location job categories for a given transaction ID.
 *
 * @async
 * @function getAllTransactionLocationJobCategories
 * @param {string} transactionId - The ID of the transaction to fetch job categories for.
 * @throws {Error} Throws an error if the transactionId is not provided or if the API call fails.
 * @returns {Promise<Object>} A promise that resolves to the data containing transaction location job categories.
 */
export const getAllTransactionLocationJobCategories = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
      const url = `${baseUrl}/getalltransactionlocationjobcategories?id=${encodeURIComponent(transactionId)}`;
      const response = await api.get(url);
      return response.data;
  } catch (error) {
      logger.error('Error getting transaction location staff', error);
      throw error;
  }
};

/**
 * Saves transaction location job categories to the server.
 *
 * @async
 * @function saveTransactionLocationJobCategories
 * @param {Object} transactionLocationJobCategories - The transaction location job categories data to be saved.
 * @throws {Error} Throws an error if the required parameter is missing or if the API call fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const saveTransactionLocationJobCategories = async(transactionLocationJobCategories) => {
  if (!transactionLocationJobCategories) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/savetransactionlocationjobcategories`;
    const response = await api.post(url, transactionLocationJobCategories);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction location staff', error);
    throw error;
  }
};

/**
 * Inserts a transaction location job category into the system.
 *
 * @async
 * @function insertTransactionLocationJobCategory
 * @param {Object} locationJobCategory - The location job category data to be inserted.
 * @throws {Error} Throws an error if the required parameter is missing or if the API call fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const insertTransactionLocationJobCategory = async(locationJobCategory) => {
  if (!locationJobCategory) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactionlocationfte`;
    const response = await api.post(url, locationJobCategory);
    return response.data;
  }
  catch (error) {
    logger.error('Error saving transaction location staff', error);
    throw error;
  }
};

/**
 * Updates the transaction location job category with the provided data.
 *
 * @async
 * @function updateTransactionLocationJobCategory
 * @param {Object} transactionLocationFte - The data object containing transaction location FTE details.
 * @throws {Error} Throws an error if the required parameter is missing or if the API call fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const updateTransactionLocationJobCategory = async(transactionLocationFte) => {
  if (!transactionLocationFte) {
    throw new Error('Missing required parameters');
  }
  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionlocationfte`;
    const response = await api.post(url, transactionLocationFte);
    return response.data;
  }
  catch (error) {
    logger.error('Error updating transaction location staff', error);
    throw error;
  }
};

/**
 * Updates the transaction terms by sending a POST request to the server.
 *
 * @async
 * @function updateTransactionTerms
 * @param {Object} transactionTerms - The transaction terms to be updated.
 * @throws {Error} Throws an error if the `transactionTerms` parameter is missing.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} A promise that resolves to the response data from the server.
 */
export const updateTransactionTerms = async(transactionTerms) => {
  if (!transactionTerms) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionterms`;
    const response = await api.post(url, transactionTerms);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction terms information', error);
    throw error;
  }
};

/**
 * Retrieves the documents associated with a specific transaction.
 *
 * @async
 * @function
 * @param {string} transactionId - The ID of the transaction to retrieve documents for.
 * @returns {Promise<DocumentModel[]>} A promise that resolves to an array of DocumentModel instances.
 * @throws {Error} Throws an error if the transactionId is missing, the response data format is invalid, 
 *                 or if there is an issue during the API call.
 */
export const getTransactionDocuments = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactiondocuments?id=${encodeURIComponent(transactionId)}`;
    const response = await api.get(url);
    if (Array.isArray(response.data)) {
      return response.data.map(item => new DocumentModel(item));
    } else {
      throw new Error('Invalid response data format');
    }
  } catch (error) {
    logger.error('Error getting transaction documents', error);
    throw error;
  }
};

/**
 * Transforms a document object into a specific request format.
 *
 * @param {Object} document - The document object to transform.
 * @param {string} document.fileName - The name of the file.
 * @param {string} document.fileType - The type of the file.
 * @param {number} document.fileSize - The size of the file in bytes.
 * @param {number} document.documentTypeId - The ID of the document type.
 * @returns {Object} The transformed document in the request format.
 * @returns {Object} return.request - The request object containing document details.
 * @returns {string} return.request.filename - The name of the file.
 * @returns {string} return.request.filetype - The type of the file.
 * @returns {number} return.request.filesize - The size of the file in bytes.
 * @returns {number} return.request.documenttypeid - The ID of the document type.
 */
const transformDocument = (document) => {
  return {
    request: {
      filename: document.fileName,
      filetype: document.fileType,
      filesize: document.fileSize,
      documenttypeid: document.documentTypeId,
    },
  };
};

/**
 * Attaches documents to a transaction by uploading them to the server.
 *
 * @async
 * @function attachTransactionDocuments
 * @param {string} transactionId - The ID of the transaction to which the documents will be attached.
 * @param {Array<Object>} documents - An array of document objects to be attached. Each document must contain a `file` property.
 * @throws {Error} Throws an error if `transactionId` or `documents` is missing, or if a document is missing its `file` property.
 * @throws {Error} Throws an error if the upload process fails.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of responses from the server for each uploaded document.
 */
export const attachTransactionDocuments = async(transactionId, documents) => {
  if (!transactionId || !documents) {
    throw new Error('Missing required parameters');
  }

  try {

    await checkToken();
    const store = useAuthStore();
    const uploadPromises = documents.map(async (doc) => {
      if (!doc.file ) {
        throw new Error('Missing file for a document');
      }

      const transformedData = transformDocument(doc);
      const formData = new FormData();
      formData.append('file', doc.file);
      formData.append('request', JSON.stringify(transformedData.request));

      const response = await axios.post(
        `${baseUrl}/attachtransactiondocument?id=${encodeURIComponent(transactionId)}`,
        formData,
        { 
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${store.token}`,
          },
        }
      );

      return response.data;
    });
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    logger.error('Error attaching report document', error);
    throw error;
  }
};

/**
 * Deletes a transaction document by its ID.
 *
 * @async
 * @function deleteTransactionDocument
 * @param {string} documentId - The ID of the document to be deleted.
 * @throws {Error} Throws an error if the documentId is not provided.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const deleteTransactionDocument = async(documentId) => {
  if (!documentId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactiondocument?id=${encodeURIComponent(documentId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction document', error);
    throw error;
  }
};

/**
 * Retrieves the transaction providers for a given transaction ID.
 *
 * @async
 * @function getTransactionProviders
 * @param {string} transactionId - The ID of the transaction to retrieve providers for.
 * @throws {Error} Throws an error if the transactionId is not provided.
 * @throws {Error} Throws an error if there is an issue with the API request.
 * @returns {Promise<Object>} A promise that resolves to the data containing the transaction providers.
 */
export const getTransactionProviders = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactionproviders?id=${encodeURIComponent(transactionId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction providers', error);
    throw error;
  }
};

/**
 * Retrieves the transaction provider details for a given transaction and provider ID.
 *
 * @async
 * @function getTransactionProvider
 * @param {string} transactionId - The unique identifier of the transaction.
 * @param {string} providerId - The unique identifier of the provider.
 * @returns {Promise<Object>} A promise that resolves to the transaction provider details.
 * @throws {Error} Throws an error if required parameters are missing or if the API request fails.
 */
export const getTransactionProvider = async(transactionId, providerId) => {
  if (!transactionId || !providerId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactionprovider?id=${encodeURIComponent(transactionId)}&providerId=${encodeURIComponent(providerId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction provider', error);
    throw error;
  }
};

/**
 * Fetches the transaction provider services for a given provider ID.
 *
 * @async
 * @function getTransactionProviderServices
 * @param {string} providerId - The ID of the provider whose services are to be retrieved.
 * @throws {Error} Throws an error if the providerId is not provided.
 * @throws {Error} Throws an error if there is an issue with the API request.
 * @returns {Promise<Object>} A promise that resolves to the data containing the transaction provider services.
 */
export const getTransactionProviderServices = async(providerId) => {
  if (!providerId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactionproviderservices?providerId=${encodeURIComponent(providerId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction provider services', error);
    throw error;
  }
};

/**
 * Inserts or updates a transaction provider.
 *
 * @async
 * @function insertUpdateTransactionProvider
 * @param {Object} transactionProvider - The transaction provider object to be inserted or updated.
 * @throws {Error} Throws an error if the `transactionProvider` parameter is missing.
 * @throws {Error} Throws an error if there is an issue during the API call.
 * @returns {Promise<Object>} The response data from the API after successfully saving the transaction provider.
 */
export const insertUpdateTransactionProvider = async(transactionProvider) => {
  if (!transactionProvider) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/insertupdatetransactionprovider`;
    const response = await api.post(url, transactionProvider);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction provider', error);
    throw error;
  }
};

/**
 * Deletes a transaction provider by its ID.
 *
 * @async
 * @function deleteTransactionProvider
 * @param {string} providerId - The ID of the provider to delete.
 * @throws {Error} Throws an error if the providerId is not provided.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const deleteTransactionProvider = async(providerId) => {
  if (!providerId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/deletetransactionprovider?providerId=${encodeURIComponent(providerId)}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    logger.error('Error deleting transaction provider (ApiService)', error);
    throw error;
  }
};

/**
 * Fetches the revenue and expense data for transaction locations based on the provided transaction ID.
 *
 * @async
 * @function getTransactionLocationsRevenue
 * @param {string} transactionId - The ID of the transaction to retrieve data for.
 * @throws {Error} Throws an error if the transactionId is not provided.
 * @throws {Error} Throws an error if there is an issue with the API request.
 * @returns {Promise<Object>} A promise that resolves to the response data containing transaction locations revenue and expense details.
 */
export const getTransactionLocationsRevenue = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactionlocationsrevenueexpense?id=${encodeURIComponent(transactionId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction locations revenue', error);
    throw error;
  }
};

/**
 * Updates the transaction location revenue or expense.
 *
 * @async
 * @function updateTransactionLocationRevene
 * @param {Object} locationRevenue - The location revenue data to be updated.
 * @throws {Error} Throws an error if the required parameter is missing or if the API call fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const updateTransactionLocationRevene = async(locationRevenue) => {
  if (!locationRevenue) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionlocationrevenueexpense`;
    const response = await api.post(url, locationRevenue);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction location revenue', error);
    throw error;
  }
};

/**
 * Retrieves the list of requested information for a specific transaction.
 *
 * @async
 * @function getTransactionRequestedInformationAll
 * @param {string} transactionId - The ID of the transaction to retrieve information for.
 * @throws {Error} Throws an error if the transactionId is not provided.
 * @throws {Error} Throws an error if there is an issue with the API request.
 * @returns {Promise<Object>} A promise that resolves to the data containing the requested information list.
 */
export const getTransactionRequestedInformationAll = async(transactionId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactionrequestedinformationlist?id=${encodeURIComponent(transactionId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction requested information list', error);
    throw error;
  }
};

/**
 * Retrieves the requested information for a specific transaction.
 *
 * @async
 * @function
 * @param {string} transactionId - The ID of the transaction to retrieve information for.
 * @param {string} requestId - The ID of the specific request associated with the transaction.
 * @throws {Error} Throws an error if the `transactionId` parameter is missing.
 * @throws {Error} Throws an error if the API call fails.
 * @returns {Promise<Object>} A promise that resolves to the data of the requested transaction information.
 */
export const getTransactionRequestedInformation = async(transactionId, requestId) => {
  if (!transactionId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/gettransactionrequestedinformation?id=${encodeURIComponent(transactionId)}&requestId=${encodeURIComponent(requestId)}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('Error getting transaction requested information', error);
    throw error;
  }
};

/**
 * Inserts transaction requested information into the system.
 *
 * @async
 * @function insertTransactionRequestedInformation
 * @param {Object} requestedInformation - The requested information to be inserted.
 * @throws {Error} Throws an error if the `requestedInformation` parameter is missing.
 * @throws {Error} Throws an error if there is an issue during the API call.
 * @returns {Promise<Object>} The response data from the API.
 */
export const insertTransactionRequestedInformation = async(requestedInformation) => {
  if (!requestedInformation) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/inserttransactionrequestedinformation`;
    const response = await api.post(url, requestedInformation);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction requested information', error);
    throw error;
  }
};

/**
 * Updates the requested information for a transaction.
 *
 * @async
 * @function
 * @param {Object} requestedInformation - The information to update for the transaction.
 * @throws {Error} Throws an error if the `requestedInformation` parameter is missing.
 * @throws {Error} Throws an error if the API request fails.
 * @returns {Promise<Object>} The response data from the API.
 */
export const updateTransactionRequestedInformation = async(requestedInformation) => {
  if (!requestedInformation) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const url = `${baseUrl}/updatetransactionrequestedinformation`;
    const response = await api.post(url, requestedInformation);
    return response.data;
  } catch (error) {
    logger.error('Error saving transaction requested information', error);
    throw error;
  }
};

/**
 * Uploads an Excel file containing data for a transaction.
 *
 * @async
 * @function
 * @param {Integer} id - The id for the transaction.
 * @param {} file - The file to upload data for the transaction.
 * @returns {UploadResponse} The response data from the API.
 */
export const uploadTransactionFile = async (id, file) => {
  validateRequiredParams({ id, file });

  const formData = new FormData();
  formData.append('file', file);
  
  const url = `/hce/transactionfileupload?id=${encodeURIComponent(id)}`;
  return await apiPostForm(url, formData);
};
